import React, { useState, useEffect } from 'react';
import { mockUsers } from '../mockData';
import MockAPI from '../mockApi';
import './DemoAccountSwitcher.scss';

interface DemoAccountSwitcherProps {
  onUserChange?: () => void;
}

const DemoAccountSwitcher: React.FC<DemoAccountSwitcherProps> = ({ onUserChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);

  useEffect(() => {
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      const response = await MockAPI.me();
      setCurrentUser(response.user);
    } catch (error) {
      setCurrentUser(null);
    }
  };

  const switchToUser = async (userId: string) => {
    try {
      const response = await MockAPI.switchUser(userId);
      setIsOpen(false);
      if (onUserChange) {
        onUserChange();
      }

      // Redirect to appropriate dashboard based on role
      const role = response.user.role;
      if (role === 'WRITER') {
        window.location.href = '/writer/dashboard';
      } else if (role === 'READER') {
        window.location.href = '/reader/dashboard';
      } else if (role === 'AGENT') {
        window.location.href = '/agent/dashboard';
      } else if (role === 'AGENCY') {
        window.location.href = '/agency/dashboard';
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Failed to switch user:', error);
    }
  };

  const logout = async () => {
    try {
      await MockAPI.logout();
      setCurrentUser(null);
      setIsOpen(false);
      if (onUserChange) {
        onUserChange();
      }
      window.location.href = '/';
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const resetDemo = () => {
    if (window.confirm('This will reset all demo data to its initial state. Continue?')) {
      MockAPI.resetDemoData();
      window.location.href = '/';
    }
  };

  const groupedUsers = {
    WRITER: mockUsers.filter(u => u.role === 'WRITER'),
    READER: mockUsers.filter(u => u.role === 'READER'),
    AGENT: mockUsers.filter(u => u.role === 'AGENT'),
  };

  return (
    <div className="demo-account-switcher">
      <button
        className="demo-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Switch Demo Account"
      >
        <span className="demo-icon">👤</span>
        <span className="demo-text">
          {currentUser ? `${currentUser.name} (${currentUser.role})` : 'Demo Account'}
        </span>
      </button>

      {isOpen && (
        <>
          <div className="demo-overlay" onClick={() => setIsOpen(false)} />
          <div className="demo-menu">
            <div className="demo-menu-header">
              <h3>Switch Demo Account</h3>
              <button className="demo-close" onClick={() => setIsOpen(false)}>×</button>
            </div>

            <div className="demo-menu-content">
              <div className="demo-current-user">
                {currentUser ? (
                  <>
                    <p><strong>Current User:</strong></p>
                    <p>{currentUser.name} ({currentUser.email})</p>
                    <p className="demo-role-badge demo-role-{currentUser.role.toLowerCase()}">
                      {currentUser.role}
                    </p>
                  </>
                ) : (
                  <p>Not logged in</p>
                )}
              </div>

              <div className="demo-user-groups">
                {Object.entries(groupedUsers).map(([role, users]) => (
                  <div key={role} className="demo-user-group">
                    <h4>{role}S</h4>
                    <div className="demo-user-list">
                      {users.map(user => (
                        <button
                          key={user.id}
                          className={`demo-user-button ${currentUser?.id === user.id ? 'active' : ''}`}
                          onClick={() => switchToUser(user.id)}
                        >
                          <div className="demo-user-info">
                            <div className="demo-user-name">{user.name}</div>
                            <div className="demo-user-email">{user.email}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="demo-menu-actions">
                {currentUser && (
                  <button className="demo-action-button logout" onClick={logout}>
                    Logout
                  </button>
                )}
                <button className="demo-action-button reset" onClick={resetDemo}>
                  Reset Demo Data
                </button>
              </div>

              <div className="demo-info">
                <p><strong>Demo Mode:</strong> All data is stored locally. No real backend is used.</p>
                <p>Click any user above to instantly switch accounts and see different views of the platform.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DemoAccountSwitcher;
